/**
 * EventListCards — a report's page of AI events as a GALLERY, the same shape the alert list
 * uses, for the same reason: an AI event is a claim about something a camera SAW, and the
 * frame settles it faster than any row of columns can describe it.
 *
 * Paired with the report table, not a replacement for it. The table is how you compare
 * twenty-five events on one screen — the confidences down a column, the plates aligned. The
 * cards are how you find the one you're after. The report page keeps both and remembers which.
 *
 * WHAT A CARD SAYS, and where it says it:
 *   • over the frame — WHEN (top right), then WHAT the model called this event and WHERE, in
 *     the scrim along the bottom. Those three are on every event of every model, so they are
 *     the part of the card that never moves.
 *   • under it — the model's own headline facts, taken from the SAME per-model columns the
 *     table and the record use (`splitEventFacts`), so a plate or a violation type can't read
 *     one way here and another way there. Timestamp and camera are dropped from that list:
 *     they're already over the picture, and a card that prints them twice wastes the two
 *     lines a face's identity or a fire's type should have had.
 *
 * The frame is a button and so is "Details" — the picture is the obvious way in, and the
 * named control is there for the reader who doesn't know that.
 */
import type { ReactNode } from 'react';
import {
  ArrowRightIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { useAlertImage } from '../alerts/useAlertImage';
import { whenStamp } from '../alerts/alertVocab';
import {
  eventTypeLabel,
  splitEventFacts,
  type EventColumnLike,
  type EventFact,
  type EventValueContext,
} from './EventDetail';

/** Already over the picture — printing them again under it would cost two of four fact slots. */
const OVERLAID = new Set(['event_timestamp', 'camera_id']);

export interface EventListCardsProps {
  events: any[];
  /** The model's column definitions — the card's facts come from these. */
  columns: EventColumnLike[];
  loading?: boolean;
  /** Camera and person name resolvers, as the record uses them. */
  context?: EventValueContext;
  /** The event's frame, as a URL the browser can load directly (null → placeholder). */
  imageFor?: (event: any) => string | null;
  /** Lazy resolver for hosts that must stream frames (the cloud, through its node tunnel). */
  loadImage?: (event: any) => Promise<string | null>;
  /** The big line over the frame. Defaults to what the model called the event. */
  titleFor?: (event: any) => string;
  /** How many facts to print under the frame. Four fills two tidy rows. */
  factLimit?: number;
  onOpen: (index: number) => void;
  /** One extra control on a card's foot — e.g. "Register" on an unknown face. */
  cardAction?: (event: any) => ReactNode;
  /** Replaces the empty state's sentence (a dead deep link says something different). */
  emptyTitle?: string;
  emptyHint?: ReactNode;
}

/** Fills whatever width it is given rather than a fixed column count — see AlertListCards. */
const GRID = 'grid gap-4 p-4 [grid-template-columns:repeat(auto-fill,minmax(19rem,1fr))]';

function EventCardMedia({
  event,
  title,
  camera,
  imageFor,
  loadImage,
}: {
  event: any;
  title: string;
  camera: string;
  imageFor?: (event: any) => string | null;
  loadImage?: (event: any) => Promise<string | null>;
}) {
  const { src, markFailed, ref } = useAlertImage(event, imageFor, loadImage);

  return (
    <div ref={ref} className="relative aspect-video w-full overflow-hidden bg-neutral-900">
      {/* Painted whether or not a frame lands, so the white type over it never flips contrast
          when one does. */}
      <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-neutral-950" />
      {src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={markFailed}
        />
      ) : (
        <PhotoIcon className="absolute left-1/2 top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2 text-white/20" />
      )}
      <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/85 via-black/45 to-transparent" />

      <div className="absolute inset-x-0 top-0 flex justify-end p-2.5">
        <span className="rounded-md bg-black/60 px-2 py-1 text-[11px] font-medium tabular-nums text-white">
          {whenStamp(event?.event_timestamp)}
        </span>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-3">
        <p className="truncate text-[15px] font-semibold leading-tight text-white" title={title}>
          {title}
        </p>
        <p className="mt-1 flex items-center gap-1.5 text-[12px] text-white/80">
          <VideoCameraIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate" title={camera}>{camera || '—'}</span>
        </p>
      </div>
    </div>
  );
}

export default function EventListCards({
  events,
  columns,
  loading,
  context = {},
  imageFor,
  loadImage,
  titleFor,
  factLimit = 4,
  onOpen,
  cardAction,
  emptyTitle = 'No events found for the selected filters.',
  emptyHint,
}: EventListCardsProps) {
  if (loading) {
    return (
      <div className={GRID}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="h-72 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
    );
  }

  if (!events.length) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
        <MagnifyingGlassIcon className="h-8 w-8 text-muted-foreground opacity-40" />
        <p className="text-sm font-medium text-foreground">{emptyTitle}</p>
        {emptyHint && <p className="text-[13px] text-muted-foreground">{emptyHint}</p>}
      </div>
    );
  }

  return (
    <div className={GRID}>
      {events.map((event, idx) => {
        const { facts, fields } = splitEventFacts(event, columns, context);
        // Headline facts first, then the supporting ones to fill the tiles the model didn't
        // earn — a person_count event has no identity or direction, and four empty slots
        // would make it look like a broken card rather than a quiet one.
        const shown: EventFact[] = [...facts, ...fields]
          .filter((f) => !OVERLAID.has(f.key))
          .slice(0, factLimit);
        const camera = context.cameraName?.(event?.camera_id) || String(event?.camera_id || '').slice(0, 8);
        const title = titleFor?.(event) || eventTypeLabel(event) || 'Event';
        const action = cardAction?.(event);

        return (
          <article
            key={event.id || idx}
            className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card transition-shadow hover:shadow-card-hover"
          >
            <button
              type="button"
              onClick={() => onOpen(idx)}
              aria-label={`Open ${title}`}
              className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
            >
              <EventCardMedia
                event={event}
                title={title}
                camera={camera}
                imageFor={imageFor}
                loadImage={loadImage}
              />
            </button>

            {shown.length > 0 && (
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 px-4 py-3">
                {shown.map((f) => (
                  <div key={f.key} className="min-w-0">
                    <dt className="truncate text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {f.label}
                    </dt>
                    <dd className={`truncate text-[13px] text-foreground ${f.mono ? 'font-mono' : ''}`}>
                      {f.value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}

            {/* `mt-auto` pins the foot down, so cards in a row line their controls up however
                many facts the model above them had. */}
            <div className="mt-auto flex items-center justify-between gap-2 border-t border-border bg-muted/30 px-3 py-2">
              <div className="flex min-w-0 items-center gap-1">{action}</div>
              <button
                type="button"
                onClick={() => onOpen(idx)}
                className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[13px] font-semibold text-brand transition-colors hover:bg-muted"
              >
                Details
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}
